import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import Button from '../ui/Button';
import { downloadCsv } from '../../utils/csvExport';
import DirectorCsvImportCard from './DirectorCsvImportCard';
import DirectorManagerRosterImportCard from './DirectorManagerRosterImportCard';
import UnifiedCsvImportCard from './UnifiedCsvImportCard';
import DirectorPanelState from './DirectorPanelState';
import { derivePeriodStartFromContext } from './performanceRows';
import { DIRECTOR_DATA_UPDATED_EVENT } from '../../utils/directorEvents';
import { formatTimestamp } from '../../utils/formatDate';
import { buildQuery } from '../../utils/buildQuery';

const ALL_DIRECTOR_LOCATIONS = 'ALL';

function buildScopedParams(selectedLocationCode, performanceContext) {
  const periodStart = derivePeriodStartFromContext(performanceContext);
  const qs = buildQuery({
    locationCode: selectedLocationCode && selectedLocationCode !== ALL_DIRECTOR_LOCATIONS ? selectedLocationCode : null,
    periodType: performanceContext?.periodType,
    periodStart
  });
  return { qs, periodStart };
}

function DirectorDataManagementPanel({
  locationLabel = 'Company Rollup',
  selectedLocationCode = ALL_DIRECTOR_LOCATIONS,
  performanceContext,
  managerRows = [],
  requestRows = [],
  attendanceRows = [],
  benchmarkRows = [],
  onNavigate
}) {
  const [importLogs, setImportLogs] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  const periodStart = useMemo(
    () => derivePeriodStartFromContext(performanceContext),
    [performanceContext]
  );

  const loadImportHistory = useCallback(async () => {
    setIsHistoryLoading(true);
    setHistoryError('');

    try {
      const qs = buildQuery({
        action: 'import',
        limit: 50,
        locationCode: selectedLocationCode !== ALL_DIRECTOR_LOCATIONS ? selectedLocationCode : null
      });

      const rows = await apiRequest(`/api/audit/director-actions?${qs}`);
      setImportLogs(rows);
    } catch (requestError) {
      setHistoryError(requestError.message || 'Unable to load import history.');
    } finally {
      setIsHistoryLoading(false);
    }
  }, [selectedLocationCode]);

  useEffect(() => {
    loadImportHistory();
    const handleDirectorDataUpdated = () => {
      loadImportHistory();
    };

    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    return () => {
      window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    };
  }, [loadImportHistory]);

  const runDataAction = useCallback(async (action, successMessage) => {
    try {
      setActionError('');
      setActionMessage('');
      await action();
      if (successMessage) {
        setActionMessage(successMessage);
      }
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to complete CSV action.');
    }
  }, []);

  const exportGoals = useCallback(async () => {
    const { qs } = buildScopedParams(selectedLocationCode, performanceContext);
    const goalRows = await apiRequest(`/api/goals/location?${qs}`);
    downloadCsv(
      `goals-${String(locationLabel || 'all').replace(/\s+/g, '-').toLowerCase()}.csv`,
      [
        { label: 'location_code', value: 'locationCode' },
        { label: 'kpi_key', value: 'kpiKey' },
        { label: 'period_type', value: 'goalPeriodType' },
        { label: 'year', value: 'goalYear' },
        { label: 'month', value: row => row.goalMonth ?? '' },
        { label: 'quarter', value: row => row.goalQuarter ?? '' },
        { label: 'week', value: row => row.goalWeek ?? '' },
        { label: 'period_start', value: 'periodStart' },
        { label: 'goal_value', value: 'goalValue' },
        { label: 'is_locked', value: row => (row.isLocked ? 'true' : 'false') }
      ],
      goalRows
    );
  }, [locationLabel, performanceContext, selectedLocationCode]);

  const exportAllocations = useCallback(async () => {
    const { qs } = buildScopedParams(selectedLocationCode, performanceContext);
    const [goalRows, managers] = await Promise.all([
      apiRequest(`/api/goals/location?${qs}`),
      apiRequest('/api/managers')
    ]);
    const distributableGoals = goalRows.filter(goal => goal.isDistributable);
    const allocationSets = await Promise.all(
      distributableGoals.map(async (goal) => apiRequest(`/api/goals/location/${goal.id}/allocations`))
    );
    const managerById = new Map(managers.map(manager => [manager.id, manager]));
    const allocations = allocationSets.flat();

    downloadCsv(
      `allocations-${String(locationLabel || 'all').replace(/\s+/g, '-').toLowerCase()}.csv`,
      [
        { label: 'location_code', value: 'locationCode' },
        { label: 'kpi_key', value: 'kpiKey' },
        { label: 'period_type', value: 'goalPeriodType' },
        { label: 'year', value: 'goalYear' },
        { label: 'month', value: row => row.goalMonth ?? '' },
        { label: 'quarter', value: row => row.goalQuarter ?? '' },
        { label: 'week', value: row => row.goalWeek ?? '' },
        { label: 'period_start', value: 'periodStart' },
        { label: 'manager_id', value: 'managerId' },
        { label: 'employee_id', value: row => managerById.get(row.managerId)?.employeeId || '' },
        { label: 'manager_email', value: row => managerById.get(row.managerId)?.email || '' },
        { label: 'allocated_value', value: 'allocatedValue' },
        { label: 'allocation_type', value: 'allocationType' }
      ],
      allocations
    );
  }, [locationLabel, performanceContext, selectedLocationCode]);

  const exportActuals = useCallback(async () => {
    const { qs } = buildScopedParams(selectedLocationCode, performanceContext);
    const [actualRows, managers] = await Promise.all([
      apiRequest(`/api/actuals?${qs}`),
      apiRequest('/api/managers')
    ]);
    const managerById = new Map(managers.map(manager => [manager.id, manager]));

    downloadCsv(
      `actuals-${String(locationLabel || 'all').replace(/\s+/g, '-').toLowerCase()}.csv`,
      [
        { label: 'location_code', value: 'locationCode' },
        { label: 'kpi_key', value: 'kpiKey' },
        { label: 'period_type', value: 'actualPeriodType' },
        { label: 'year', value: 'actualYear' },
        { label: 'month', value: row => row.actualMonth ?? '' },
        { label: 'quarter', value: row => row.actualQuarter ?? '' },
        { label: 'week', value: row => row.actualWeek ?? '' },
        { label: 'period_start', value: 'periodStart' },
        { label: 'manager_id', value: 'managerId' },
        { label: 'employee_id', value: row => managerById.get(row.managerId)?.employeeId || '' },
        { label: 'manager_email', value: row => managerById.get(row.managerId)?.email || '' },
        { label: 'actual_date', value: 'actualDate' },
        { label: 'actual_value', value: 'actualValue' },
        { label: 'notes', value: row => row.notes || '' }
      ],
      actualRows
    );
  }, [locationLabel, performanceContext, selectedLocationCode]);

  const exportManagerRoster = useCallback(() => {
    downloadCsv(
      `manager-roster-${String(locationLabel || 'all').replace(/\s+/g, '-').toLowerCase()}.csv`,
      [
        { label: 'Name', value: 'name' },
        { label: 'Role', value: 'roleTitle' },
        { label: 'Email', value: 'email' },
        { label: 'Location', value: 'locationCode' },
        { label: 'Hire Date', value: row => row.hireDate || '' },
        { label: 'Status', value: row => row.status || 'active' }
      ],
      managerRows
    );
  }, [locationLabel, managerRows]);

  const exportRequests = useCallback(() => {
    downloadCsv(
      `requests-${String(locationLabel || 'all').replace(/\s+/g, '-').toLowerCase()}.csv`,
      [
        { label: 'Requester', value: 'managerName' },
        { label: 'Location', value: 'locationCode' },
        { label: 'Event Type', value: row => String(row.eventType || '').replace(/_/g, ' ') },
        { label: 'Start Date', value: 'startDate' },
        { label: 'End Date', value: 'endDate' },
        { label: 'Time Block', value: 'timeBlock' },
        { label: 'Status', value: 'status' },
        { label: 'Reason', value: 'reason' }
      ],
      requestRows
    );
  }, [locationLabel, requestRows]);

  const exportAttendance = useCallback(() => {
    downloadCsv(
      `attendance-incidents-${String(locationLabel || 'all').replace(/\s+/g, '-').toLowerCase()}-${performanceContext?.periodType || 'period'}.csv`,
      [
        { label: 'Manager', value: 'managerName' },
        { label: 'Location', value: 'locationCode' },
        { label: 'Event Type', value: 'eventType' },
        { label: 'Status', value: 'status' },
        { label: 'Start Date', value: 'startDate' },
        { label: 'End Date', value: 'endDate' },
        { label: 'Time Block', value: 'timeBlock' },
        { label: 'Reason', value: 'reason' }
      ],
      attendanceRows
    );
  }, [attendanceRows, locationLabel, performanceContext?.periodType]);

  const exportBenchmark = useCallback(() => {
    downloadCsv(
      `benchmark-vacation-${String(locationLabel || 'all').replace(/\s+/g, '-').toLowerCase()}.csv`,
      [
        { label: 'Manager', value: 'managerName' },
        { label: 'Location', value: 'locationCode' },
        { label: 'Hire Date', value: 'hireDate' },
        { label: 'Years of Service', value: row => row.yearsOfService ?? '' },
        { label: 'Benchmark Annual Days', value: 'benchmarkAnnualDays' },
        { label: 'Approved YTD', value: 'approvedDaysTakenYtd' },
        { label: 'Scheduled', value: 'scheduledUpcomingDays' },
        { label: 'Total', value: 'totalApprovedAndScheduled' },
        { label: 'Variance', value: 'varianceVsBenchmark' },
        { label: 'Benchmark Flag', value: 'benchmarkFlag' }
      ],
      benchmarkRows
    );
  }, [benchmarkRows, locationLabel]);

  const exportAudit = useCallback(async () => {
    const qs = buildQuery({
      limit: 500,
      locationCode: selectedLocationCode !== ALL_DIRECTOR_LOCATIONS ? selectedLocationCode : null
    });
    const logs = await apiRequest(`/api/audit/director-actions?${qs}`);
    downloadCsv('director_audit_log.csv', [
      { label: 'Time', value: (row) => formatTimestamp(row.createdAt) },
      { label: 'Actor', value: 'actorEmail' },
      { label: 'Action', value: (row) => String(row.action || '').replace(/_/g, ' ') },
      { label: 'Entity Type', value: 'entityType' },
      { label: 'Entity', value: (row) => row.entityLabel || row.entityType },
      { label: 'Summary', value: 'summary' }
    ], logs);
  }, [selectedLocationCode]);

  return (
    <div className="director-admin-stack">
      <section className="director-card">
        <div className="role-section-header">
          <h2>Data Management</h2>
          <span>{performanceContext?.periodType || 'period'} / {periodStart || 'no period selected'}</span>
        </div>
        <p className="role-section-copy">
          Manage bulk CSV imports and exports here.
        </p>
        <div className="director-toolbar">
          <div className="director-toolbar-group">
            <label>Quick Links</label>
            <div className="director-table-actions">
              <Button size="sm" variant="secondary" onClick={() => onNavigate?.('/goals')}>Goals</Button>
              <Button size="sm" variant="secondary" onClick={() => onNavigate?.('/allocations')}>Allocations</Button>
              <Button size="sm" variant="secondary" onClick={() => onNavigate?.('/actuals')}>Actuals</Button>
              <Button size="sm" variant="secondary" onClick={() => onNavigate?.('/managers')}>Managers</Button>
              <Button size="sm" variant="secondary" onClick={() => onNavigate?.('/requests-calendar')}>Requests &amp; Calendar</Button>
            </div>
          </div>
        </div>
      </section>

      <UnifiedCsvImportCard onApplied={loadImportHistory} />

      <div className="director-split-grid director-phase-two-grid">
        <DirectorCsvImportCard
          entityType="goals"
          title="Import Goals CSV"
          description="Preview and apply goal updates for the active performance context."
          columnHint="location_code,kpi_key,period_type,year,month,period_start,goal_value,is_locked"
        />
        <DirectorCsvImportCard
          entityType="allocations"
          title="Import Allocations CSV"
          description="Preview and apply explicit manager allocations without leaving the data operations center."
          columnHint="location_code,kpi_key,period_type,year,month,period_start,manager_id,employee_id,manager_email,allocated_value,allocation_type"
        />
      </div>

      <div className="director-split-grid director-phase-two-grid">
        <DirectorCsvImportCard
          entityType="actuals"
          title="Import Actuals CSV"
          description="Preview and apply actual updates while preserving the same period model used by scorecards."
          columnHint="location_code,kpi_key,period_type,year,month,period_start,manager_id,employee_id,manager_email,actual_date,actual_value,notes"
        />
        <DirectorManagerRosterImportCard
          selectedLocationCode={selectedLocationCode}
          locationLabel={locationLabel}
        />
      </div>

      <section className="director-card">
        <div className="role-section-header">
          <h2>Export Center</h2>
          <span>{locationLabel}</span>
        </div>
        <DirectorPanelState kind="success" message={!actionError ? actionMessage : ''} />
        <DirectorPanelState kind="error" message={actionError} />
        <div className="director-toolbar">
          <div className="director-toolbar-group">
            <label>Performance</label>
            <div className="director-table-actions">
              <Button size="sm" variant="secondary" onClick={() => runDataAction(exportGoals, 'Goals CSV downloaded.')}>Export Goals</Button>
              <Button size="sm" variant="secondary" onClick={() => runDataAction(exportAllocations, 'Allocations CSV downloaded.')}>Export Allocations</Button>
              <Button size="sm" variant="secondary" onClick={() => runDataAction(exportActuals, 'Actuals CSV downloaded.')}>Export Actuals</Button>
            </div>
          </div>
          <div className="director-toolbar-group">
            <label>Operations</label>
            <div className="director-table-actions">
              <Button size="sm" variant="secondary" onClick={() => runDataAction(exportManagerRoster, 'Leadership directory CSV downloaded.')} disabled={managerRows.length === 0}>Export Leadership Directory</Button>
              <Button size="sm" variant="secondary" onClick={() => runDataAction(exportRequests, 'Requests CSV downloaded.')} disabled={requestRows.length === 0}>Export Requests</Button>
              <Button size="sm" variant="secondary" onClick={() => runDataAction(exportAttendance, 'Attendance CSV downloaded.')} disabled={attendanceRows.length === 0}>Export Attendance</Button>
            </div>
          </div>
          <div className="director-toolbar-group">
            <label>Reporting</label>
            <div className="director-table-actions">
              <Button size="sm" variant="secondary" onClick={() => runDataAction(exportBenchmark, 'Benchmark CSV downloaded.')} disabled={benchmarkRows.length === 0}>Export Benchmark</Button>
              <Button size="sm" variant="secondary" onClick={() => runDataAction(exportAudit, 'Audit log CSV downloaded.')}>Export Audit Log</Button>
            </div>
          </div>
        </div>
      </section>

      <section className="director-card">
        <div className="role-section-header">
          <h2>Import History</h2>
          <span>{importLogs.length} recent imports</span>
        </div>
        <DirectorPanelState kind="loading" message={isHistoryLoading ? 'Loading import history...' : ''} />
        <DirectorPanelState kind="error" message={historyError} />
        <div className="table-card">
          <table className="role-table director-import-history-table">
            <thead>
              <tr>
                <th className="director-import-history-col-time">Time</th>
                <th className="director-import-history-col-actor">Actor</th>
                <th className="director-import-history-col-entity">Entity</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {importLogs.length ? importLogs.map(log => (
                <tr key={log.id}>
                  <td className="director-import-history-col-time">{formatTimestamp(log.createdAt)}</td>
                  <td className="director-import-history-col-actor">{log.actorEmail}</td>
                  <td className="director-import-history-col-entity">{log.entityLabel || log.entityType}</td>
                  <td>{log.summary}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4">No CSV import actions match the current scope yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default DirectorDataManagementPanel;
